import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';

import { api } from '../lib/api.js';
import { Cargando, Error as AvisoError } from '../piezas/Estados.jsx';

/**
 * Las tarifas de un proyecto, por tipo de servicio.
 *
 * La tabla existia desde la migracion 0005 y NADIE la escribia: no habia API
 * ni pantalla. Dos de las siete medidas de la consulta pivote —ingreso y
 * margen— no tenian de donde salir, y no habia forma de arreglarlo desde la
 * plataforma.
 *
 * Se listan TODOS los tipos de servicio, tambien los que no tienen tarifa.
 * Es la diferencia entre ver lo que falta capturar y que lo que falta
 * simplemente no aparezca: lo segundo no se nota hasta que el reporte de
 * ingreso sale corto y nadie sabe por que.
 */

interface Renglon {
  tipo_servicio_id: string;
  clave: string;
  nombre: string;
  tarifa_centavos: string | null;
  moneda: string | null;
}

/** Pesos a centavos, sin flotantes de por medio. */
function aCentavos(texto: string): number | null {
  const limpio = texto.replace(/[^\d.]/g, '').trim();
  if (limpio === '') return null;
  const partes = limpio.split('.');
  if (partes.length > 2) return null;
  const enteros = partes[0] ?? '0';
  /* Dos decimales, rellenando: "1.5" son 150 centavos, no 15. Y se corta en
     dos, que un tercer decimal en dinero no significa nada. */
  const decimales = (partes[1] ?? '').padEnd(2, '0').slice(0, 2);
  const n = Number(`${enteros}${decimales}`);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

const aPesos = (centavos: string | null): string =>
  centavos === null ? '' : (Number(centavos) / 100).toFixed(2);

export function Tarifas({
  folio,
  puedeEscribir,
  alCerrar,
}: {
  folio: string;
  puedeEscribir: boolean;
  /**
   * Solo cuando este panel se dibuja por su cuenta. Dentro del dialogo del
   * proyecto se omite: ese dialogo ya tiene su propio cierre y dos botones
   * de cerrar a un palmo uno del otro cierran cosas distintas.
   */
  alCerrar?: (() => void) | undefined;
}): JSX.Element {
  const [filas, setFilas] = useState<Renglon[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [edicion, setEdicion] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState<string | null>(null);

  /**
   * Recarga desde el servidor.
   *
   * `soloEsta` dice que renglon se acaba de guardar. Sin eso, al recargar se
   * reescribian TODOS los campos con lo que hay en la base, y si alguien
   * tenia otro renglon a medio escribir lo perdia sin aviso al guardar el
   * primero. Ahora lo escrito a mano se respeta y solo se sincroniza lo que
   * la persona acaba de confirmar.
   */
  const cargar = useCallback(
    (soloEsta?: string) => {
      api
        .obtener<Renglon[]>(`/v1/proyectos/${encodeURIComponent(folio)}/tarifas`)
        .then(({ datos }) => {
          setFilas(datos);
          setEdicion((previo) =>
            Object.fromEntries(
              datos.map((r) => [
                r.clave,
                soloEsta === undefined || r.clave === soloEsta
                  ? aPesos(r.tarifa_centavos)
                  : (previo[r.clave] ?? aPesos(r.tarifa_centavos)),
              ]),
            ),
          );
        })
        .catch(setError);
    },
    [folio],
  );

  useEffect(() => cargar(), [cargar]);

  async function guardar(r: Renglon): Promise<void> {
    const centavos = aCentavos(edicion[r.clave] ?? '');
    setGuardando(r.clave);
    try {
      if (centavos === null) {
        /* Vaciar el campo QUITA la tarifa. Es lo que la persona espera al
           borrarlo, y la alternativa —guardar cero— seria peor: un servicio
           que vale cero y uno sin precio capturado se ven igual en un
           reporte y significan cosas distintas. */
        if (r.tarifa_centavos !== null) {
          await api.quitar(`/v1/proyectos/${encodeURIComponent(folio)}/tarifas/${r.clave}`);
        }
      } else {
        await api.poner(`/v1/proyectos/${encodeURIComponent(folio)}/tarifas/${r.clave}`, {
          tarifa_centavos: centavos,
        });
      }
      cargar(r.clave);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: '14px' }} data-prueba="panel-tarifas">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Tarifas · {folio}</h2>
        <span className="sp" />
        <span className="mini">
          Precio por tipo de servicio. De aqui salen el ingreso y el margen de la consulta pivote
        </span>
        {alCerrar && (
          <button
            type="button"
            className="btn ghost"
            onClick={alCerrar}
            data-prueba="cerrar-tarifas"
          >
            Cerrar
          </button>
        )}
      </div>

      {error !== null ? (
        <AvisoError error={error} />
      ) : filas === null ? (
        <Cargando />
      ) : (
        <div className="tabla-mapa">
          <table data-prueba="tabla-tarifas">
            <thead>
              <tr>
                <th scope="col">Tipo de servicio</th>
                <th scope="col" className="num">
                  Tarifa (MXN)
                </th>
                {puedeEscribir && <th scope="col" />}
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.clave} data-prueba="fila-tarifa">
                  <th scope="row">
                    {r.nombre}
                    <div className="mini">{r.clave}</div>
                  </th>
                  <td className="num">
                    {puedeEscribir ? (
                      <input
                        value={edicion[r.clave] ?? ''}
                        onChange={(e) => setEdicion({ ...edicion, [r.clave]: e.target.value })}
                        /* Este campo vive dentro del formulario del
                           proyecto, asi que un Enter aqui enviaria ESE
                           formulario: se daria de alta o se guardaria el
                           proyecto cuando la persona solo queria fijar una
                           tarifa. Enter guarda la tarifa del renglon. */
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          if ((edicion[r.clave] ?? '') !== aPesos(r.tarifa_centavos)) {
                            void guardar(r);
                          }
                        }}
                        inputMode="decimal"
                        placeholder="sin tarifa"
                        style={{ width: 110, textAlign: 'right' }}
                        data-prueba={`tarifa-${r.clave}`}
                      />
                    ) : r.tarifa_centavos === null ? (
                      <span className="muted">sin tarifa</span>
                    ) : (
                      aPesos(r.tarifa_centavos)
                    )}
                  </td>
                  {puedeEscribir && (
                    <td>
                      <button
                        type="button"
                        className="btn sm"
                        onClick={() => void guardar(r)}
                        disabled={
                          guardando === r.clave ||
                          (edicion[r.clave] ?? '') === aPesos(r.tarifa_centavos)
                        }
                        data-prueba={`guardar-${r.clave}`}
                      >
                        {guardando === r.clave ? '...' : 'Guardar'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
